import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Button,
  Paper,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  Search, 
  ShoppingCart, 
  FilterList,
  Sort,
  ViewModule,
  ViewList,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { postcardApi } from '../services/api';
import { getTemplates, getTemplatesByCategory } from '../services/templateService';
import { Template } from '../types/template';
import GalleryPostcard from '../components/GalleryPostcard';
import GalleryTemplate from '../components/GalleryTemplate';
import { useAuth } from '../hooks/useAuth';
import { Postcard } from '../types/postcard';


const categories = [
  'All',
  'Birthday',
  'Holiday',
  'Thank You',
  'Congratulations',
  'Sympathy',
  'Custom',
];

const Gallery = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated, user } = useAuth();
  const [postcards, setPostcards] = useState<Postcard[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('newest');
  const [cart, setCart] = useState<{ [key: string]: number }>({});
  const [activeTab, setActiveTab] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(!isMobile);

  useEffect(() => {
    if (activeTab === 0) {
      fetchTemplates();
    } else if (isAuthenticated) {
      fetchPostcards();
    }
  }, [page, sortBy, activeTab, selectedCategory, isAuthenticated]);

  const fetchPostcards = async () => {
    try {
      setLoading(true);
      const response = await postcardApi.getAll();
      setPostcards(response.data);
    } catch (error) {
      console.error('Error fetching postcards:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      let templatesData: Template[];
      
      if (selectedCategory && selectedCategory !== 'All') {
        templatesData = await getTemplatesByCategory(selectedCategory);
      } else {
        templatesData = await getTemplates();
      }
      
      setTemplates(templatesData);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(1);
    if (activeTab === 0) {
      fetchTemplates();
    } else {
      fetchPostcards();
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleSortChange = (event: any) => {
    setSortBy(event.target.value);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPage(1);
  };

  const handleCategoryChange = (category: string | null) => {
    setSelectedCategory(category);
    setPage(1);
  };

  const goToCheckout = () => {
    navigate('/checkout');
  };

  const renderPostcards = () => {
    if (!isAuthenticated) {
      return (
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 3, md: 6 }, 
            textAlign: 'center',
            borderRadius: 2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Sign in to view your postcards
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Create beautiful postcards from our templates and manage them all in one place.
          </Typography>
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            justifyContent="center"
            sx={{ mt: 4 }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/login')}
              size="large"
            >
              Sign In
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate('/register')}
              size="large"
            >
              Create Account
            </Button>
          </Stack>
        </Paper>
      );
    }

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Grid container spacing={3}>
          {postcards.length === 0 && (
            <Grid item xs={12}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: { xs: 3, md: 6 }, 
                  textAlign: 'center',
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  You haven't created any postcards yet.
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                  Browse our templates and create your first postcard!
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setActiveTab(0)}
                  sx={{ mt: 3 }}
                  size="large"
                >
                  View Templates
                </Button>
              </Paper>
            </Grid>
          )}
          {postcards.map((postcard) => (
            <Grid 
              item 
              xs={12} 
              sm={viewMode === 'grid' ? 6 : 12} 
              md={viewMode === 'grid' ? 4 : 12} 
              key={postcard.id}
            >
              <GalleryPostcard postcard={postcard} viewMode={viewMode} />
            </Grid>
          ))}
        </Grid>
      </motion.div>
    );
  };

  const renderTemplates = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Grid container spacing={3}>
        {templates.map((template) => (
          <Grid 
            item 
            xs={12} 
            sm={viewMode === 'grid' ? 6 : 12} 
            md={viewMode === 'grid' ? 4 : 12} 
            key={template.id}
          >
            <GalleryTemplate 
              template={template} 
              onUseTemplate={(id) => navigate(`/create/${id}`)}
              viewMode={viewMode}
            />
          </Grid>
        ))}
      </Grid>
    </motion.div>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
          Gallery
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Browse our collection of templates or view your created postcards
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          aria-label="gallery tabs"
          variant={isMobile ? "fullWidth" : "standard"}
        >
          <Tab label="Templates" />
          <Tab label="My Postcards" disabled={!isAuthenticated} />
        </Tabs>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <form onSubmit={handleSearch}>
              <TextField
                fullWidth
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ bgcolor: 'background.paper' }}
              />
            </form>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack 
              direction="row" 
              spacing={2} 
              justifyContent={{ xs: 'space-between', md: 'flex-end' }}
              alignItems="center"
            >
              {isMobile && (
                <IconButton 
                  onClick={() => setShowFilters(!showFilters)}
                  color="primary"
                >
                  <FilterList />
                </IconButton>
              )}
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={handleSortChange}
                  size="small"
                >
                  <MenuItem value="newest">Newest</MenuItem>
                  <MenuItem value="popular">Most Popular</MenuItem>
                  <MenuItem value="name">Name</MenuItem>
                </Select>
              </FormControl>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Grid View">
                  <IconButton 
                    onClick={() => setViewMode('grid')}
                    color={viewMode === 'grid' ? 'primary' : 'default'}
                  >
                    <ViewModule />
                  </IconButton>
                </Tooltip>
                <Tooltip title="List View">
                  <IconButton 
                    onClick={() => setViewMode('list')}
                    color={viewMode === 'list' ? 'primary' : 'default'}
                  >
                    <ViewList />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {!isMobile && (
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {categories.map((category) => (
              <Chip
                key={category}
                label={category}
                onClick={() => handleCategoryChange(category === 'All' ? null : category)}
                color={selectedCategory === category ? 'primary' : 'default'}
                variant={selectedCategory === category ? 'filled' : 'outlined'}
                sx={{ m: 0.5 }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {isMobile && showFilters && (
        <Paper 
          elevation={0}
          sx={{ 
            p: 2, 
            mb: 4,
            borderRadius: 2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Categories
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {categories.map((category) => (
              <Chip
                key={category}
                label={category}
                onClick={() => handleCategoryChange(category === 'All' ? null : category)}
                color={selectedCategory === category ? 'primary' : 'default'}
                variant={selectedCategory === category ? 'filled' : 'outlined'}
                size="small"
                sx={{ m: 0.5 }}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {activeTab === 0 ? renderTemplates() : renderPostcards()}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default Gallery; 